import { useState } from "react";
import { ChevronDown, ChevronRight, Code, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

interface ElDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
  isLoading: boolean;
  title?: string;
}

const JsonNode = ({ value, depth = 0 }: { value: any; depth?: number }) => {
  const [collapsed, setCollapsed] = useState(depth > 1);

  if (value === null) return <span className="text-gray-400">null</span>;
  if (value === undefined) return <span className="text-gray-400">undefined</span>;
  if (typeof value === "boolean") return <span className="text-purple-500">{String(value)}</span>;
  if (typeof value === "number") return <span className="text-blue-500">{value}</span>;
  if (typeof value === "string") return <span className="text-slate-600">"{value}"</span>;

  const isArray = Array.isArray(value);
  const entries = isArray ? value.map((v: any, i: number) => [i, v]) : Object.entries(value);
  const isEmpty = entries.length === 0;
  const brackets = isArray ? ["[", "]"] : ["{", "}"];

  if (isEmpty) return <span className="text-gray-500">{brackets[0]}{brackets[1]}</span>;

  return (
    <span>
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="inline-flex items-center gap-0.5 hover:text-genie-primary transition-colors"
      >
        {collapsed
          ? <ChevronRight className="h-3 w-3 text-gray-400" />
          : <ChevronDown className="h-3 w-3 text-gray-400" />}
        <span className="text-gray-500">{brackets[0]}</span>
        {collapsed && (
          <span className="text-gray-400 ml-0.5">
            {isArray ? `${entries.length} items` : `${entries.length} keys`}
          </span>
        )}
      </button>
      {collapsed ? (
        <span className="text-gray-500">{brackets[1]}</span>
      ) : (
        <>
          <div style={{ paddingLeft: "1.25rem" }}>
            {entries.map(([key, val]: [any, any], i: number) => (
              <div key={key} className="leading-6">
                {!isArray && (
                  <span className="text-black font-medium">"{key}"</span>
                )}
                {!isArray && <span className="text-gray-500">: </span>}
                <JsonNode value={val} depth={depth + 1} />
                {i < entries.length - 1 && <span className="text-gray-400">,</span>}
              </div>
            ))}
          </div>
          <span className="text-gray-500">{brackets[1]}</span>
        </>
      )}
    </span>
  );
};

export const ElDetailsModal = ({ isOpen, onClose, data, isLoading, title = "ElevenLabs Agent Response" }: ElDetailsModalProps) => {
  const handleCopyData = async () => {
    if (!data) {
      toast.error("No data to copy");
      return;
    }

    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[95vw] h-[95vh] max-w-none flex flex-col">
        <DialogHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Raw agent data viewer
          </DialogDescription>
          <Button
            onClick={handleCopyData}
            variant="outline"
            size="sm"
            disabled={!data || isLoading}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </DialogHeader>
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-genie-primary" />
            </div>
          ) : data ? (
            <div className="bg-genie-background-muted rounded-lg p-4 border border-genie-border font-mono text-xs leading-6 text-genie-text-primary">
              <JsonNode value={data} depth={0} />
            </div>
          ) : (
            <div className="text-center py-8 text-genie-text-secondary">No data available</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
